import {
  Component,
  computed,
  HostBinding,
  Inject,
  signal,
  WritableSignal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  DataScope,
  DefaultDataProcessingFeatures,
  DefaultDataScope,
  IQBoostViewStore,
  IQDataProcessingFeatures,
  ViewType,
  AiFeaturesService,
} from '@maximizer/ai/data-access';
import {
  ConfigurationService,
  FeatureService,
} from '@maximizer/core/shared/data-access';
import {
  CDN_URL,
  ConfigurationSetting,
  Octopus,
  TranslationListItem,
} from '@maximizer/core/shared/domain';
import { DisposableComponent } from '@maximizer/core/shared/ui/components/base';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import { cloneObject } from '@maximizer/shared/util';
import { TranslatePipe } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_DROPDOWNLIST } from '@progress/kendo-angular-dropdowns';
import { KENDO_INPUTS } from '@progress/kendo-angular-inputs';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import { forkJoin, Observable, tap } from 'rxjs';
import {
  EventAction,
  EventJourney,
  IQBoostEventTracker,
} from '../classes/event-tracker';
import { IQBoostTextVersion } from '../classes/iqboost';

type Requests = {
  user: Observable<ConfigurationSetting | null>;
  admin?: Observable<ConfigurationSetting | null>;
};

@Component({
  selector: 'ai-iqboost-privacy-security-center',
  templateUrl: './iqboost-privacy-security-center.component.html',
  imports: [
    FormsModule,
    KENDO_BUTTON,
    KENDO_DROPDOWNLIST,
    KENDO_INPUTS,
    KENDO_LABEL,
    UI_KENDO,
    TranslatePipe,
  ],
})
export class IqboostPrivacySecurityCenterComponent extends DisposableComponent {
  @HostBinding('class') hostClasses = 'ai-ng';
  aiClass = true;

  isFFGlobalModelsEnabled = signal(true);
  isFFOnlineResourcesEnabled = signal(true);
  dataScope: WritableSignal<DataScope> = signal(DefaultDataScope);
  dataProcessingFeatures: WritableSignal<IQDataProcessingFeatures> = signal(DefaultDataProcessingFeatures);
  
  dueDates = signal([] as TranslationListItem<string>[]);
  selectedDueDate?: TranslationListItem<string>;

  textVersion = computed(() => 
    this.aiFeatures.isSLE() 
      ? IQBoostTextVersion.defaultVersion 
      : IQBoostTextVersion.faVersion
  );
  isAdmin = computed(() => this.store.dataProcessingFeatureGroup().isAdmin);
  isGlobalModelsEnabledByAdmin = computed(() => this.store.dataProcessingFeatureGroup().admin.dataProcessingFeatures.useGlobalServers);
  isOnlineResourcesEnabledByAdmin = computed(() => this.store.dataProcessingFeatureGroup().admin.dataProcessingFeatures.useWebData);

  constructor(
    public readonly store: IQBoostViewStore,
    public readonly tracker: IQBoostEventTracker,
    private readonly feature: FeatureService,
    private readonly aiFeatures: AiFeaturesService,
    private readonly configurationService: ConfigurationService,
    @Inject(CDN_URL) readonly cdn: string,
  ) { 
    super();

    const adminProcessingFeatures = this.store.dataProcessingFeatureGroup().admin.dataProcessingFeatures;

    if (this.isAdmin()) {
      this.dataProcessingFeatures.set(cloneObject(adminProcessingFeatures));
    }
    else {
      const userProcessingFeatures = this.store.dataProcessingFeatureGroup().user.dataProcessingFeatures;
      this.dataProcessingFeatures.set(cloneObject({
        ...userProcessingFeatures,
        anonymized: userProcessingFeatures.anonymized || adminProcessingFeatures.anonymized,
        useGlobalServers: userProcessingFeatures.useGlobalServers && adminProcessingFeatures.useGlobalServers,
        useWebData: userProcessingFeatures.useWebData && adminProcessingFeatures.useWebData,
      }));
    }

    this.dataScope.set(cloneObject(this.store.dataScope()));
    this.isFFGlobalModelsEnabled.set(this.feature.isFeatureOn('core-ai-global-models-enabled', true));
    this.isFFOnlineResourcesEnabled.set(this.feature.isFeatureOn('core-ai-online-resources-enabled', true));
    this.loadDueDate();
  }

  private saveFiltersConfigurationUser(): Observable<ConfigurationSetting | null> {
    const processedDataScope: DataScope = {
      ...this.dataScope(),
      dataFiltering: {
        dueDate: this.selectedDueDate?.id as Octopus.DateOptionId,
      },
    };

    this.store.setDataProcessingFeatureGroup({
      ...this.store.dataProcessingFeatureGroup(),
      user: {
        ...this.store.dataProcessingFeatureGroup().user,
        dataProcessingFeatures: this.dataProcessingFeatures(),
      },
    });
    this.store.setDataScope(processedDataScope);
    return this.configurationService
      .updateSetting(
        this.store.configurationSettingUser()?.id ?? null,
        this.store.configurationSettingCodesUser(),
        JSON.stringify({
          wasFirstTimeOpenedPrivacySecurityCenter: true,
          dataProcessingFeatures: this.dataProcessingFeatures(),
          dataScope: processedDataScope,
          recentUserPrompts: this.store.dataProcessingFeatureGroup().user.recentUserPrompts ?? [],
        }),
      )
      .pipe(
        tap((setting) => setting && this.store.setConfigurationSettingUser(setting)),
      );
  }

  private saveFiltersConfigurationAdmin(): Observable<ConfigurationSetting | null> {
    this.store.setDataProcessingFeatureGroup({
      ...this.store.dataProcessingFeatureGroup(),
      admin: {
        ...this.store.dataProcessingFeatureGroup().admin,
        dataProcessingFeatures: this.dataProcessingFeatures(),
      },
    });

    return this.configurationService
      .updateSetting(
        this.store.configurationSettingAdmin()?.id ?? null,
        this.store.configurationSettingCodesAdmin(),
        JSON.stringify({
          dataProcessingFeatures: this.dataProcessingFeatures(),
        }),
      )
      .pipe(
        tap((setting) => setting && this.store.setConfigurationSettingAdmin(setting)),
      );
  }

  private loadDueDate(): void {
    const currentDueDate = this.store.dataScope().dataFiltering?.dueDate 
      ?? Octopus.DateOptionId.LASTYEAR;

    this.dueDates.set(
      Octopus.getDateOptions([
        Octopus.DateOptionId.THISWEEK,
        Octopus.DateOptionId.LASTWEEK,
        Octopus.DateOptionId.THISMONTH,
        Octopus.DateOptionId.THISANDLASTMONTH,
        Octopus.DateOptionId.LASTMONTH,
        Octopus.DateOptionId.LASTTHREEMONTHS,
        Octopus.DateOptionId.LASTSIXMONTHS,
        Octopus.DateOptionId.LASTTWELVEMONTHS,
        Octopus.DateOptionId.THISANDLAST2YEARS,
        Octopus.DateOptionId.THISANDLAST3YEARS,
        Octopus.DateOptionId.THISANDLAST4YEARS,
        Octopus.DateOptionId.THISANDLAST5YEARS,
      ]),
    );

    this.selectedDueDate = Octopus.DateOptions[currentDueDate];
  }

  private isProcessingFeaturesDifferent(a: IQDataProcessingFeatures, b: IQDataProcessingFeatures): boolean {
    return (
      a.anonymized !== b.anonymized ||
      a.useGlobalServers !== b.useGlobalServers ||
      a.useWebData !== b.useWebData
    );
  }

  private isDataScopeDifferent(a: DataScope, b: DataScope): boolean {
    return (
      a.all !== b.all ||
      a.householdDetails !== b.householdDetails ||
      a.accountDetails !== b.accountDetails ||
      a.notes !== b.notes ||
      a.emails !== b.emails ||
      a.documents !== b.documents ||
      a.calls !== b.calls ||
      a.allInteractions !== b.allInteractions ||
      a.dataFiltering?.dueDate !== (this.selectedDueDate?.id as Octopus.DateOptionId)
    );
  }

  closePrivacySecurityCenter(): void {
    this.tracker.trackEvent(
      EventJourney.SecurityCenter, 
      EventAction.Saved, 
    );

    const processingFeatures = this.isAdmin() 
      ? this.store.dataProcessingFeatureGroup().admin.dataProcessingFeatures 
      : this.store.dataProcessingFeatureGroup().user.dataProcessingFeatures;
    const isProcessingFeaturesDifferent = this.isProcessingFeaturesDifferent(this.dataProcessingFeatures(), processingFeatures);
    const isDataScopeDifferent = this.isDataScopeDifferent(this.dataScope(), this.store.dataScope());

    if (
      isDataScopeDifferent || 
      isProcessingFeaturesDifferent || 
      !this.store.dataProcessingFeatureGroup().user.wasFirstTimeOpenedPrivacySecurityCenter
    ) {
      this.store.setLoading(true);
      
      let requests: Requests = {
        user: this.saveFiltersConfigurationUser(),
      };
      
      if (this.isAdmin()) {
        requests = {
          ...requests,
          admin: this.saveFiltersConfigurationAdmin(),
        };
      }

      forkJoin(requests)
        .pipe(
          tap(() => {
            if (isDataScopeDifferent) {
              this.store.triggerScopeChange({
                useAllHouseholds: this.store.useAllHouseholds(),
              });
            } else {
              this.store.setLoading(false);
            }
          }),
          tap(() => this.store.setViewType(ViewType.Report)),
        )
        .subscribe();
    }
    else {
      this.store.setViewType(ViewType.Report);
    }
  }

} 
